import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "react-query";
import {
  DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams,
  GridRowSelectionModel, Toolbar, useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Chip, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import MergeIcon from "@mui/icons-material/Merge";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { axiosInstance } from "api/axios";
import registryInvoicesService from "api/registryInvoicesService";
import bankingService from "api/bankingService";
import contractorsService from "api/contractorsService";
import useCustomQuery from "hooks/useCustomQuery";
import { IFinancialOperation } from "dto/Banking/IBanking";
import { plGridLocales } from "utils/muiGridLocales";
import Search from "assets/icons/search.gif";
import InfoIcon from "assets/icons/info_icon.svg?react";
import ContractorMetricSlide, { ContractorSlideData } from "./ContractorMetricSlide";
import ContractorDetailPanel, { LinkedInvoice } from "./ContractorDetailPanel";
import { ProposalEntry, PROPOSAL_SOURCE_STYLE, ManualNipInput } from "./nipProposals";
import {
  ContractorRow,
  fetchAllKSeFPages,
  loadFkAccountLookup,
  lookupFkAcct,
  computeContractorAmounts,
} from "./contractorUtils";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

export type { ProposalEntry, ContractorRow };

// ── Normalizacja ─────────────────────────────────────────────────────────────

const normalizeNip  = (s: string) => s.replace(/\D/g, "");
const normalizeIban = (s: string) => s.replace(/\s/g, "").toUpperCase();

const addIban = (ibans: string[], raw: string) => {
  if (!raw) return;
  const n = normalizeIban(raw);
  if (n && !ibans.includes(n)) ibans.push(n);
};

// ── Budowanie mapy kontrahentów ───────────────────────────────────────────────

function buildContractorsMap(
  purchaseInvoices: unknown[],
  salesInvoices: unknown[],
  registryInvoices: unknown[],
  bankOperations: IFinancialOperation[],
): Map<string, ContractorRow> {
  const map = new Map<string, ContractorRow>();

  const ensureByNip = (
    nip: string, name: string, ibanRaw: string,
    source: string, invoice: LinkedInvoice,
  ) => {
    const n = normalizeNip(nip);
    if (n.length !== 10) return;
    if (!map.has(n)) {
      map.set(n, { id: n, name: name.trim(), nip: n, ibans: [], docCount: 0, sources: [], invoices: [], bankOps: [] });
    }
    const c = map.get(n)!;
    if (!c.name && name.trim()) c.name = name.trim();
    addIban(c.ibans, ibanRaw);
    c.docCount++;
    if (!c.sources.includes(source)) c.sources.push(source);
    c.invoices.push(invoice);
  };

  for (const inv of purchaseInvoices as Record<string, unknown>[]) {
    const seller = inv.Seller as Record<string, string> | undefined;
    ensureByNip(seller?.Nip ?? "", seller?.Name ?? "", String(inv.BankAccount ?? ""), "Zakup", {
      id: String(inv.Id ?? ""),
      number: String(inv.InvoiceNumber ?? inv.KsefNumber ?? inv.Id ?? ""),
      date: String(inv.IssueDate ?? ""),
      grossAmount: Number(inv.GrossAmount ?? inv.Amount ?? 0),
      currency: String(inv.Currency ?? "PLN"),
      type: "Zakup",
      ksefNumber: inv.KsefNumber ? String(inv.KsefNumber) : undefined,
    });
  }

  for (const inv of salesInvoices as Record<string, unknown>[]) {
    const buyer = inv.Buyer as Record<string, unknown> | undefined;
    const buyerNip = (buyer?.Identifier as Record<string, string> | undefined)?.Value ?? "";
    ensureByNip(buyerNip, String(buyer?.Name ?? ""), String(inv.BankAccount ?? ""), "Sprzedaż", {
      id: String(inv.Id ?? ""),
      number: String(inv.InvoiceNumber ?? inv.KsefNumber ?? inv.Id ?? ""),
      date: String(inv.IssueDate ?? ""),
      grossAmount: Number(inv.GrossAmount ?? inv.Amount ?? 0),
      currency: String(inv.Currency ?? "PLN"),
      type: "Sprzedaż",
      ksefNumber: inv.KsefNumber ? String(inv.KsefNumber) : undefined,
    });
  }

  for (const inv of registryInvoices as Record<string, unknown>[]) {
    ensureByNip(
      String(inv.IssuerNIP ?? ""),
      String(inv.IssuerCompany ?? inv.IssuerFullName ?? ""),
      String(inv.AccountNumber ?? ""),
      "Rejestr",
      {
        id: String(inv.Id ?? ""),
        number: String(inv.InvoiceNumber ?? inv.Id ?? ""),
        date: String(inv.IssueDate ?? ""),
        grossAmount: Number(inv.GrossAmount ?? inv.NetAmount ?? inv.Amount ?? 0),
        currency: String(inv.Currency ?? "PLN"),
        type: "Rejestr",
        ksefNumber: inv.KsefNumber ? String(inv.KsefNumber) : undefined,
        fileUri: inv.FileUri ? String(inv.FileUri) : undefined,
        encryptionKeyId: inv.EncryptionKeyId ? String(inv.EncryptionKeyId) : undefined,
        fileName: inv.FileName ? String(inv.FileName) : undefined,
        objectKeys: (inv.ObjectKeys as import("dto/IKey").IKey[] | undefined) ?? undefined,
      },
    );
  }

  // ── Operacje bankowe ──────────────────────────────────────────────────────
  const ibanIndex = new Map<string, ContractorRow>();
  for (const c of map.values()) {
    for (const ib of c.ibans) ibanIndex.set(ib, c);
  }

  // Indeks po nazwie dla kontrahentów bez IBAN (mBank i inne banki nie zawsze go podają)
  const nameIndex = new Map<string, ContractorRow>();

  for (const op of bankOperations) {
    const source = op.Direction === "Credit" ? "Wpływ" : "Wypływ";
    const iban = op.CounterpartyIban ? normalizeIban(op.CounterpartyIban) : "";
    const name = op.CounterpartyName?.trim() ?? "";

    if (iban) {
      // ── Ścieżka IBAN (priorytet) ─────────────────────────────────────────
      const matched = ibanIndex.get(iban);
      if (matched) {
        if (!matched.sources.includes(source)) matched.sources.push(source);
        matched.bankOps.push(op);
        if (!nameIndex.has(name.toLowerCase()) && name) nameIndex.set(name.toLowerCase(), matched);
      } else {
        const key = `IBAN:${iban}`;
        if (!map.has(key)) {
          map.set(key, { id: key, name: name, nip: "", ibans: [iban], docCount: 0, sources: [], invoices: [], bankOps: [] });
          ibanIndex.set(iban, map.get(key)!);
          if (name) nameIndex.set(name.toLowerCase(), map.get(key)!);
        }
        const c = map.get(key)!;
        if (!c.name && name) c.name = name;
        c.docCount++;
        if (!c.sources.includes(source)) c.sources.push(source);
        c.bankOps.push(op);
      }
    } else if (name) {
      // ── Ścieżka NAME (fallback gdy brak IBAN) ────────────────────────────
      const nameKey = name.toLowerCase();
      const matched = nameIndex.get(nameKey);
      if (matched) {
        if (!matched.sources.includes(source)) matched.sources.push(source);
        matched.bankOps.push(op);
      } else {
        const key = `NAME:${name}`;
        if (!map.has(key)) {
          map.set(key, { id: key, name: name, nip: "", ibans: [], docCount: 0, sources: [], invoices: [], bankOps: [] });
          nameIndex.set(nameKey, map.get(key)!);
        }
        const c = map.get(key)!;
        c.docCount++;
        if (!c.sources.includes(source)) c.sources.push(source);
        c.bankOps.push(op);
      }
    }
  }

  return map;
}

// ── Ręczne przypisania NIP (localStorage) ────────────────────────────────────

const NIP_OVERRIDES_LS_KEY = "contractors_nip_overrides_v1";
type NipOverrides = Record<string, string>; // { rowId → nip }

const loadNipOverrides = (): NipOverrides => {
  try { return JSON.parse(localStorage.getItem(NIP_OVERRIDES_LS_KEY) ?? "{}"); }
  catch { return {}; }
};

const saveNipOverride = (rowId: string, nip: string) => {
  const overrides = loadNipOverrides();
  overrides[rowId] = nip;
  localStorage.setItem(NIP_OVERRIDES_LS_KEY, JSON.stringify(overrides));
};

function applyNipOverrides(map: Map<string, ContractorRow>, overrides: NipOverrides): void {
  for (const [rowId, nip] of Object.entries(overrides)) {
    const row = map.get(rowId);
    if (row && !row.nip) row.nip = nip; // nie nadpisuj NIP-u z faktury
  }
}

// ── Trwałość scaleń (localStorage) ───────────────────────────────────────────
// Format: { [absorbedId]: leaderId } — płaski indeks każdej wchłoniętej pozycji

const MERGES_LS_KEY = "contractors_merges_v1";
type MergeMap = Record<string, string>;

const loadMergeMap = (): MergeMap => {
  try { return JSON.parse(localStorage.getItem(MERGES_LS_KEY) ?? "{}"); }
  catch { return {}; }
};

const saveMergeMap = (m: MergeMap) =>
  localStorage.setItem(MERGES_LS_KEY, JSON.stringify(m));

function applyPersistedMerges(
  map: Map<string, ContractorRow>,
  mergeMap: MergeMap,
): void {
  // Grupuj wchłonięte ID po liderze
  const byLeader = new Map<string, string[]>();
  for (const [absId, lId] of Object.entries(mergeMap)) {
    if (!byLeader.has(lId)) byLeader.set(lId, []);
    byLeader.get(lId)!.push(absId);
  }

  for (const [leaderId, absorbedIds] of byLeader) {
    const leader = map.get(leaderId);
    if (!leader) continue;

    for (const absId of absorbedIds) {
      const absorbed = map.get(absId);
      if (!absorbed) continue;
      for (const ib of absorbed.ibans) {
        if (!leader.ibans.includes(ib)) leader.ibans.push(ib);
      }
      for (const src of absorbed.sources) {
        if (!leader.sources.includes(src)) leader.sources.push(src);
      }
      leader.invoices.push(...absorbed.invoices);
      leader.bankOps.push(...absorbed.bankOps);
      leader.docCount += absorbed.docCount;
      map.delete(absId);
    }
  }
}

// ── Scalanie zaznaczonych kontrahentów ────────────────────────────────────────

function mergeSelected(selected: ContractorRow[]): ContractorRow {
  // Pozycja wiodąca = pierwsza z NIP; jeśli brak — pierwsza na liście
  const leader = selected.find((c) => c.nip) ?? selected[0];

  const allIbans = [...new Set(selected.flatMap((c) => c.ibans))];
  const allSources = [...new Set(selected.flatMap((c) => c.sources))];
  const allInvoices = selected.flatMap((c) => c.invoices);
  const allBankOps = selected.flatMap((c) => c.bankOps);
  const docCount = selected.reduce((s, c) => s + c.docCount, 0);

  return {
    ...leader,
    ibans: allIbans,
    sources: allSources,
    docCount,
    invoices: allInvoices,
    bankOps: allBankOps,
  };
}

// ── Chip: kolor i tło według źródła ──────────────────────────────────────────

const SOURCE_STYLE: Record<string, { color: string; bg: string }> = {
  "Zakup":    { color: "#1565c0", bg: "rgba(21,101,192,0.10)" },
  "Sprzedaż": { color: "#2e7d32", bg: "rgba(46,125,50,0.10)" },
  "Rejestr":  { color: "#7b1fa2", bg: "rgba(123,31,162,0.10)" },
  "Wpływ":    { color: "#00695c", bg: "rgba(0,105,92,0.10)" },
  "Wypływ":   { color: "#e65100", bg: "rgba(230,81,0,0.10)" },
};
const DEFAULT_SOURCE_STYLE = { color: "#5f5c71", bg: "rgba(95,92,113,0.10)" };

// ── Formatowanie IBAN do wyświetlenia ─────────────────────────────────────────

function fmtIban(raw: string): string {
  if (!raw) return "";
  const digits = raw.replace(/^PL/i, "").replace(/\D/g, "");
  if (digits.length !== 26) return raw;
  const groups = digits.match(/.{1,4}/g) ?? [];
  return `PL ${groups.join(" ")}`;
}

// ── Nakładka "brak danych" ────────────────────────────────────────────────────

const CustomNoRowsOverlay = () => (
  <div className={styles.empty}>
    <img src={Search} alt="" />
    <div>Brak kontrahentów. Sprawdź czy faktury KSeF są zsynchronizowane.</div>
  </div>
);

// ── Komponent główny ──────────────────────────────────────────────────────────

const ContractorsRegister: FC<Props> = ({ setSubtitle }) => {
  const apiRef = useGridApiRef();

  const [rows, setRows] = useState<ContractorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [metricOf, setMetricOf] = useState<ContractorSlideData | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ type: "include", ids: new Set() });
  const [editingNipId, setEditingNipId] = useState<string | null>(null);
  const [nipDraft, setNipDraft] = useState("");
  const [nipSearchMode, setNipSearchMode] = useState(false);
  const [canFinish, setCanFinish] = useState(false);
  const sessionNoNipIds = useRef<Set<string>>(new Set());
  const searchInitiatedRef = useRef<Set<string>>(new Set());

  const queryClient = useQueryClient();
  const { mutateAsync: dedupeContractors } = contractorsService.useDedupe();

  // Sprawdza czy wszystkie wiersze sesji mają rozstrzygnięte propozycje
  const checkCompletion = useCallback(() => {
    const ids = [...sessionNoNipIds.current];
    if (ids.length === 0) { setCanFinish(false); return; }
    const allResolved = ids.every(id => {
      const row = apiRef.current.getRow(id) as ContractorRow | null;
      if (!row) return true;
      if (row.proposalLoading) return false;
      if (row.proposals === undefined) return false;
      if (row.nip) return true;
      return row.proposals.length === 0;
    });
    setCanFinish(allResolved);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rejectProposal = useCallback((rowId: string, nip: string) => {
    const currentRow = apiRef.current.getRow(rowId) as ContractorRow | null;
    const updated = (currentRow?.proposals ?? []).filter(p => p.nip !== nip);
    apiRef.current.updateRows([{ id: rowId, proposals: updated }]);
    checkCompletion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkCompletion]);

  // Wyszukiwanie MF → GUS sekwencyjnie dla jednego wiersza (bez state, przez apiRef)
  const lookupNipForRow = useCallback(async (row: ContractorRow) => {
    const setProposals = (proposals: ProposalEntry[], loading: boolean) =>
      apiRef.current.updateRows([{ id: row.id, proposals, proposalLoading: loading }]);

    if (row.ibans.length > 0) {
      const account = row.ibans[0].replace(/^PL/i, "").replace(/[\s-]/g, "");
      if (account.length === 26) {
        try {
          const date = new Date().toISOString().split("T")[0];
          const res = await fetch(`https://wl-api.mf.gov.pl/api/search/bank-account/${account}?date=${date}`);
          if (res.ok) {
            const data = await res.json();
            const mfHits: ProposalEntry[] = (data?.result?.subjects ?? [])
              .map((s: Record<string, string>) => ({ nip: (s.nip ?? "").replace(/\D/g, ""), name: s.name ?? "", source: "MF" as const }))
              .filter((s: ProposalEntry) => s.nip.length === 10)
              .slice(0, 3);
            if (mfHits.length > 0) { setProposals(mfHits, false); checkCompletion(); return; }
          }
        } catch { /* fallthrough */ }
      }
    }

    if (row.name.trim().length >= 3) {
      try {
        const res = await axiosInstance.get<Array<Record<string, unknown>>>("/bir/search-by-name", { params: { name: row.name.trim() } });
        const birHits: ProposalEntry[] = (res.data ?? [])
          .map(s => ({ nip: String(s.Nip ?? s.nip ?? "").replace(/\D/g, ""), name: String(s.Name ?? s.name ?? ""), source: "GUS" as const }))
          .filter(s => s.nip.length === 10)
          .slice(0, 5);
        setProposals(birHits, false);
      } catch {
        setProposals([], false);
      }
    } else {
      setProposals([], false);
    }
    checkCompletion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkCompletion]);

  // Wszystkie źródła danych jako React Query — brak race condition (build mapy jest synchroniczny)
  const { data: registryInvoicesData } = registryInvoicesService.useGetInvoices();
  const { data: bankOpsData = [] } = useCustomQuery<IFinancialOperation[]>(
    [bankingService.queryKeys.operations, "", ""],
    async () => (await axiosInstance.get<IFinancialOperation[]>("/banking/operations")).data,
    { retry: 2 },
  );
  const { data: purchaseInvoices = [] } = useCustomQuery<unknown[]>(
    ["ksef.invoices.purchase"],
    () => fetchAllKSeFPages(1),
    { retry: 1, staleTime: 5 * 60 * 1000 },
  );
  const { data: salesInvoices = [] } = useCustomQuery<unknown[]>(
    ["ksef.invoices.sales"],
    () => fetchAllKSeFPages(0),
    { retry: 1, staleTime: 5 * 60 * 1000 },
  );


  // Odświeżenie ręczne — inwaliduje wszystkie 4 źródła
  const handleRefreshAll = () => {
    queryClient.invalidateQueries(["ksef.invoices.purchase"]);
    queryClient.invalidateQueries(["ksef.invoices.sales"]);
    queryClient.invalidateQueries([bankingService.queryKeys.operations]);
    queryClient.invalidateQueries(["registryInvoices"]);
  };

  // Buduje mapę synchronicznie po zmianie dowolnego źródła — bez race condition
  useEffect(() => {
    setLoading(true);
    const map = buildContractorsMap(
      purchaseInvoices,
      salesInvoices,
      (registryInvoicesData ?? []) as unknown[],
      bankOpsData,
    );
    applyPersistedMerges(map, loadMergeMap());
    applyNipOverrides(map, loadNipOverrides());
    const fkLookup = loadFkAccountLookup();
    const sorted = [...map.values()]
      .map((row) => ({ ...row, acctCode: lookupFkAcct(row.name, fkLookup) }))
      .sort((a, b) => a.name.localeCompare(b.name, "pl", { sensitivity: "base" }));
    setRows(sorted);
    setSubtitle(`${sorted.length} kontrahentów`);
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryInvoicesData, bankOpsData, purchaseInvoices, salesInvoices]);

  // Uruchamia wyszukiwanie propozycji NIP dla wszystkich wierszy bez NIP
  useEffect(() => {
    if (!nipSearchMode) {
      sessionNoNipIds.current = new Set();
      searchInitiatedRef.current = new Set();
      setCanFinish(false);
      return;
    }
    const noNipRows = rows.filter(r => !r.nip);
    sessionNoNipIds.current = new Set(noNipRows.map(r => r.id));
    searchInitiatedRef.current = new Set();

    noNipRows.forEach(r => apiRef.current.updateRows([{ id: r.id, proposals: undefined, proposalLoading: true }]));
    noNipRows.forEach((r, i) => {
      if (searchInitiatedRef.current.has(r.id)) return;
      searchInitiatedRef.current.add(r.id);
      setTimeout(() => lookupNipForRow(r), i * 400);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nipSearchMode]);

  const handleFinish = useCallback(async () => {
    setNipSearchMode(false);
    try {
      const res = await dedupeContractors();
      alert(res.Merged > 0
        ? `Scalono ${res.Merged} duplikatów (${res.Groups} grup).`
        : "Nie znaleziono duplikatów do scalenia.");
      handleRefreshAll();
    } catch {
      alert("Odgruzowanie nie powiodło się.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dedupeContractors]);

  // Filtrujemy po !r.nip bezpośrednio z `rows` — unikamy race condition:
  // useEffect ustawia sessionNoNipIds PO renderze, więc ref byłby pusty przy
  // pierwszym renderze i updateRows dodawałby niekompletne wiersze do gridu.
  const displayedRows = useMemo(
    () => nipSearchMode ? rows.filter(r => !r.nip) : rows,
    [rows, nipSearchMode],
  );

  // ── Scalanie ──────────────────────────────────────────────────────────────

  const selectedIds = useMemo(
    () => new Set([...selectionModel.ids].map(String)),
    [selectionModel],
  );

  const handleMerge = () => {
    if (selectedIds.size < 2) return;
    const selected = rows.filter((r) => selectedIds.has(r.id));
    const leader = selected.find((c) => c.nip) ?? selected[0];
    const absorbed = selected.filter((c) => c.id !== leader.id);

    // Zapisz scalenie do localStorage
    const mergeMap = loadMergeMap();
    for (const a of absorbed) {
      mergeMap[a.id] = leader.id;
      // Jeśli 'a' było liderem wcześniejszego scalenia — przekieruj jego wchłoniętych do nowego lidera
      for (const [absId, lId] of Object.entries(mergeMap)) {
        if (lId === a.id) mergeMap[absId] = leader.id;
      }
    }
    saveMergeMap(mergeMap);

    const merged = mergeSelected(selected);
    const removedIds = new Set(absorbed.map((c) => c.id));
    setRows((prev) =>
      [
        ...prev.filter((r) => !removedIds.has(r.id) && r.id !== leader.id),
        merged,
      ].sort((a, b) => a.name.localeCompare(b.name, "pl", { sensitivity: "base" })),
    );
    setSelectionModel({ type: "include", ids: new Set() });
  };

  const handleResetMerges = () => {
    localStorage.removeItem(MERGES_LS_KEY);
    handleRefreshAll();
  };

  const mergeEnabled = selectedIds.size >= 2 &&
    rows.filter((r) => selectedIds.has(r.id)).some((r) => r.nip);

  // ── Edycja NIP inline ────────────────────────────────────────────────────

  const commitNip = useCallback(async (row: ContractorRow, nipOverride?: string) => {
    const nip = (nipOverride ?? nipDraft).replace(/\D/g, "");
    setEditingNipId(null);
    if (nip.length !== 10) return;

    if (nipSearchMode) {
      // W trybie weryfikacji: aktualizuj tylko dotknięty wiersz przez apiRef (brak przerenderowania listy)
      apiRef.current.updateRows([{ id: row.id, nip, proposals: [], proposalLoading: false }]);
      checkCompletion();
    } else {
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, nip } : r));
    }
    saveNipOverride(row.id, nip);

    // Zapis do bazy — znajdź lub utwórz kontrahenta
    try {
      let dbId: string | null = null;

      // 1. Szukaj po nowym NIP
      try {
        const found = await axiosInstance.get<{ Id: string }>("/contractors/find", { params: { nip } });
        dbId = found.data?.Id ?? null;
      } catch { /* 404 = nie istnieje */ }

      if (dbId) {
        // Kontrahent z tym NIP istnieje — powiąż IBAN jeśli go nie ma
        const iban = row.ibans[0];
        if (iban) await axiosInstance.put(`/contractors/${dbId}`, { PrimaryIban: iban });
      } else if (row.nip) {
        // Edycja istniejącego NIP — znajdź po starym NIP i zaktualizuj
        try {
          const old = await axiosInstance.get<{ Id: string }>("/contractors/find", { params: { nip: row.nip } });
          if (old.data?.Id) await axiosInstance.put(`/contractors/${old.data.Id}`, { NIP: nip });
        } catch { /* nie znaleziono po starym NIP — utwórz nowy */ }
      } else {
        // Nowy kontrahent IBAN-only — utwórz z NIP i IBAN
        await axiosInstance.post("/contractors", {
          Name: row.name || nip,
          NIP: nip,
          ...(row.ibans[0] && { PrimaryIban: row.ibans[0] }),
        });
      }
    } catch (err) {
      console.error("[Kontrahenci] Błąd zapisu NIP do bazy:", err);
      // localStorage cache już zapisany — dane nie zostaną utracone
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nipDraft, nipSearchMode, checkCompletion]);

  // ── Detail panel ─────────────────────────────────────────────────────────

  const getDetailPanelContent = useCallback(
    (params: GridRowParams) => {
      const row = params.row as ContractorRow;
      return (
        <ContractorDetailPanel
          ibans={row.ibans}
          invoices={row.invoices}
          bankOps={row.bankOps}
          contractorId={row.id}
          onUndoMerge={async () => {}}
        />
      );
    },
    [],
  );

  const getDetailPanelHeight = useCallback(() => "auto" as const, []);

  // ── Kolumny ───────────────────────────────────────────────────────────────

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "name",
      headerName: "Nazwa kontrahenta",
      flex: 1,
      minWidth: 200,
      renderHeader: () => <div className={styles.header}>Nazwa kontrahenta</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;
        return (
          <div className={styles.nameCell} title={row.name}>
            {row.name || <span className={styles.noName}>— brak nazwy —</span>}
            <span className={styles.docCount}>{row.docCount} dok.</span>
          </div>
        );
      },
    },
    {
      field: "nip",
      headerName: "NIP",
      width: 150,
      renderHeader: () => <div className={styles.header}>NIP</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;

        if (editingNipId === row.id) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 2 }} onClick={(e) => e.stopPropagation()}>
              <input
                autoFocus
                className={styles.nipInput}
                value={nipDraft}
                maxLength={10}
                placeholder="0000000000"
                onChange={(e) => setNipDraft(e.target.value.replace(/\D/g, "").slice(0, 10))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitNip(row);
                  if (e.key === "Escape") setEditingNipId(null);
                  e.stopPropagation();
                }}
                onBlur={() => commitNip(row)}
              />
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commitNip(row)}
                sx={{ color: "#2e7d32", p: "2px", minWidth: 0 }}
              >
                <CheckIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setEditingNipId(null)}
                sx={{ color: "#c62828", p: "2px", minWidth: 0 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </div>
          );
        }

        if (!row.nip) {
          return (
            <span
              className={styles.editHint}
              onClick={(e) => {
                e.stopPropagation();
                setEditingNipId(row.id);
                setNipDraft("");
              }}
            >
              — dodaj NIP
            </span>
          );
        }

        const n = row.nip;
        return (
          <Tooltip title="Kliknij, aby edytować NIP" placement="top">
            <span
              className={styles.nipValue}
              onClick={(e) => {
                e.stopPropagation();
                setEditingNipId(row.id);
                setNipDraft(n);
              }}
            >
              {`${n.slice(0,3)}-${n.slice(3,6)}-${n.slice(6,8)}-${n.slice(8)}`}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "__nipLookup",
      headerName: "Propozycja NIP",
      width: 380,
      sortable: false, filterable: false, hideable: false,
      groupable: false, disableColumnMenu: true, disableReorder: true,
      renderHeader: () => <div className={styles.header}>Propozycja NIP (MF · GUS)</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;

        // Zaakceptowany w tej sesji — NIP już w kolumnie NIP, tutaj czysto
        if (row.nip && row.proposals !== undefined && row.proposals.length === 0) {
          return <span style={{ color: "#2e7d32", fontSize: 11, fontStyle: "italic" }}>✓ zaakceptowano</span>;
        }

        if (row.proposalLoading || row.proposals === undefined) {
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0" }}>
              <span className={styles.noData} style={{ fontStyle: "italic" }}>Szukam…</span>
              <ManualNipInput onAccept={(nip) => commitNip(row, nip)} />
            </div>
          );
        }

        const fmtNip = (n: string) => `${n.slice(0,3)}-${n.slice(3,6)}-${n.slice(6,8)}-${n.slice(8)}`;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0" }}>
            {row.proposals.length === 0 && (
              <span className={styles.noData}>Brak wyników automatycznych</span>
            )}
            {row.proposals.map(p => {
              const { color, bg } = PROPOSAL_SOURCE_STYLE[p.source];
              return (
                <div key={p.nip} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: bg, borderRadius: 10, padding: "2px 8px 2px 6px" }}>
                  <span style={{ color, fontSize: 11, fontWeight: 500, whiteSpace: "nowrap" }}>
                    [{p.source}] {p.name} · {fmtNip(p.nip)}
                  </span>
                  <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); commitNip(row, p.nip); }} sx={{ color: "#2e7d32", p: "2px", minWidth: 0 }}>
                    <CheckIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); rejectProposal(row.id, p.nip); }} sx={{ color: "#c62828", p: "2px", minWidth: 0 }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </div>
              );
            })}
            <ManualNipInput onAccept={(nip) => commitNip(row, nip)} />
          </div>
        );
      },
    },
    {
      field: "ibans",
      headerName: "Nr rachunku (IBAN)",
      width: 300,
      renderHeader: () => <div className={styles.header}>Nr rachunku (IBAN)</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;
        const ibans = row.ibans ?? [];
        if (!ibans.length) return <span className={styles.noData}>—</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontFamily: "monospace", fontSize: 11 }}>
              {fmtIban(ibans[0])}
            </span>
            {ibans.length > 1 && (
              <Tooltip
                title={
                  <div style={{ fontSize: 11, lineHeight: 1.8 }}>
                    {ibans.map((ib) => fmtIban(ib)).join("\n")}
                  </div>
                }
                placement="top"
              >
                <span className={styles.ibanCountBadge}>+{ibans.length - 1}</span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      field: "sources",
      headerName: "Źródło",
      width: 200,
      sortable: false,
      renderHeader: () => <div className={styles.header}>Źródło</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;
        return (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {(row.sources ?? []).map((s) => {
              const st = SOURCE_STYLE[s] ?? DEFAULT_SOURCE_STYLE;
              return (
                <Chip key={s} label={s} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600, color: st.color, background: st.bg }} />
              );
            })}
          </div>
        );
      },
    },
    {
      field: "__metric",
      headerName: "",
      width: 40, minWidth: 40, maxWidth: 40,
      sortable: false, filterable: false, hideable: false,
      groupable: false, disableColumnMenu: true, disableReorder: true,
      renderHeader: () => null,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;
        return (
          <Tooltip title="Metryka kontrahenta" placement="left">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const { kwotaWn, kwotaMa, saldo } = computeContractorAmounts(row);
                setMetricOf({
                  id: row.id,
                  acctCode: row.acctCode ?? (row.nip || row.ibans[0] || row.id),
                  name: row.name,
                  nip: row.nip,
                  bankAccount: row.ibans[0] ?? "",
                  docCount: row.docCount,
                  kwotaWn, kwotaMa, saldo,
                  invoices: row.invoices,
                  bankOps: row.bankOps,
                });
              }}
              sx={{ color: "#8a7fa5", "&:hover": { color: "#5d49c7", background: "rgba(93,73,199,0.08)" } }}
            >
              <InfoIcon style={{ width: 16, height: 16 }} />
            </IconButton>
          </Tooltip>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [editingNipId, nipDraft, commitNip, nipSearchMode, rejectProposal]);

  // ── Toolbar ───────────────────────────────────────────────────────────────

  const noNipCount = useMemo(() => rows.filter(r => !r.nip).length, [rows]);

  const ContractorsToolbar = () => (
    <Toolbar>
      <div className={styles.mergedToolbar}>
        <Tooltip title="Odśwież" placement="bottom">
          <button
            className={styles.addBtn}
            onClick={handleRefreshAll}
          >
            <RefreshIcon fontSize="small" />
          </button>
        </Tooltip>

        <Tooltip
          title={nipSearchMode
            ? "Zamknij tryb uzupełniania NIP"
            : `Wyszukaj i uzupełnij brakujące NIP (${noNipCount} kontrahentów bez NIP)`}
          placement="bottom"
        >
          <button
            className={nipSearchMode ? styles.mergeBtn : styles.addBtn}
            onClick={() => setNipSearchMode(m => !m)}
          >
            <ManageSearchIcon fontSize="small" />
            {noNipCount > 0 && !nipSearchMode && (
              <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 600 }}>{noNipCount}</span>
            )}
          </button>
        </Tooltip>

        {selectedIds.size >= 2 && (
          <Tooltip
            title={mergeEnabled
              ? `Scal ${selectedIds.size} zaznaczone pozycje w jedną`
              : "Zaznacz przynajmniej jedną pozycję z numerem NIP jako pozycję wiodącą"
            }
            placement="bottom"
          >
            <span>
              <button
                className={styles.mergeBtn}
                onClick={handleMerge}
                disabled={!mergeEnabled}
              >
                <MergeIcon fontSize="small" />
                Scal ({selectedIds.size})
              </button>
            </span>
          </Tooltip>
        )}

        {Object.keys(loadMergeMap()).length > 0 && selectedIds.size < 2 && (
          <Tooltip title="Cofnij wszystkie scalenia i przywróć oryginalną listę" placement="bottom">
            <button className={styles.resetMergeBtn} onClick={handleResetMerges}>
              Resetuj scalenia
            </button>
          </Tooltip>
        )}

        {nipSearchMode && canFinish && (
          <Tooltip title="Zakończ weryfikację NIP i uruchom usuwanie duplikatów" placement="bottom">
            <button className={styles.mergeBtn} onClick={handleFinish}>
              <CheckIcon fontSize="small" />
              Zakończ weryfikację
            </button>
          </Tooltip>
        )}

        <div className={styles.spacer} />

        {selectedIds.size > 0 && (
          <span className={styles.selectionInfo}>
            Zaznaczono: {selectedIds.size}
          </span>
        )}
      </div>
    </Toolbar>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.list}>
        <DataGridPremium
          apiRef={apiRef}
          localeText={plGridLocales}
          slots={{ toolbar: ContractorsToolbar, noRowsOverlay: CustomNoRowsOverlay }}
          showToolbar
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          columnHeaderHeight={30}
          columns={columns}
          columnVisibilityModel={{ __nipLookup: nipSearchMode }}
          getRowHeight={() => nipSearchMode ? "auto" as const : null}
          rows={displayedRows}
          loading={loading}
          hideFooter
          disableColumnFilter
          disablePivoting
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": { outline: "none !important" },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus": { outline: "none !important" },
            "& .MuiDataGrid-detailPanel": { overflow: "visible" },
          }}
        />
      </div>

      {metricOf && createPortal(
        <ContractorMetricSlide
          contractor={metricOf}
          wpkLookup={new Map()}
          onClose={() => setMetricOf(null)}
        />,
        document.body,
      )}
    </div>
  );
};

export default ContractorsRegister;
